import { useCallback, useEffect, useMemo, useState } from "react";
import { IndianRupee, ShoppingCart, FileQuestion, Users, Store, UserCheck, Wallet, Percent, Crown, FileCheck2, Pencil, GripVertical, Check, Plus, X, ArrowUp, ArrowDown } from "lucide-react";
import { formatCr } from "../../lib/format";

const inr = (n) => formatCr(n || 0);
const num = (n) => (Number(n) || 0).toLocaleString("en-IN");

export const KPI_DEFS = [
  { key: "revenue", label: "Revenue (Paid)", icon: IndianRupee, source: "analytics", hint: "Settled order value", value: (d) => inr(d.analytics?.revenue) },
  { key: "orders", label: "Total Orders", icon: ShoppingCart, source: "analytics", hint: "All purchase orders", value: (d) => num(d.analytics?.total_orders) },
  { key: "rfqs", label: "Active RFQs", icon: FileQuestion, source: "rfqs", hint: "Open requirements + quote requests",
    value: (d) => num((d.rfqs || []).filter((r) => r.status === "open").length + (d.quoteRequests || []).filter((q) => !["closed", "converted", "rejected", "cancelled"].includes(q.status)).length) },
  { key: "buyers", label: "Registered Buyers", icon: Users, source: "users", hint: "Empaneled buyer accounts", value: (d) => num((d.users || []).filter((u) => u.role === "buyer").length) },
  { key: "sellers", label: "Registered Sellers", icon: Store, source: "users", hint: "Empaneled seller accounts", value: (d) => num((d.users || []).filter((u) => u.role === "seller").length) },
  { key: "approvals", label: "Pending Approvals", icon: UserCheck, source: "pending", hint: "Awaiting trade-desk sign-off", value: (d) => num(d.pending?.count) },
  { key: "wallets", label: "Wallet Balances", icon: Wallet, source: "liquidity", hint: "Closed-loop ecosystem balance", value: (d) => inr(d.liquidity?.total_ecosystem_balance) },
  { key: "commission", label: "Commission Earned", icon: Percent, source: "claims", hint: "Approved + paid claims",
    value: (d) => inr((d.claims || []).filter((c) => ["approved", "paid"].includes(c.status)).reduce((s, c) => s + (c.eligible_commission_inr || 0), 0)) },
  { key: "custodians", label: "Active Custodians", icon: Crown, source: "custodians", hint: "Major Custodian kits issued", value: (d) => num((d.custodians?.accounts || []).length) },
  { key: "signed", label: "Documents Signed", icon: FileCheck2, source: "kyc", hint: "eSigned agreements on file", value: (d) => num((d.kyc || []).reduce((s, k) => s + (k.signatures || []).length, 0)) },
];
const SOURCES = {
  analytics: "/analytics", rfqs: "/rfqs", quoteRequests: "/quote-requests", users: "/users", pending: "/admin/users/pending-count",
  liquidity: "/admin/wallet/liquidity", claims: "/claims", custodians: "/admin/custodian-kits", kyc: "/kyc",
};
const DEFAULT_KPIS = ["revenue", "orders", "rfqs", "buyers", "sellers", "approvals", "wallets", "commission"];
const MAX = 8;
const storageKey = (email) => `vinci.admin.kpis.${(email || "admin").toLowerCase()}`;

export function useKpiPrefs(email) {
  const [selected, setSelected] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem(storageKey(email))); return Array.isArray(v) && v.length ? v : DEFAULT_KPIS; } catch { return DEFAULT_KPIS; }
  });
  const save = useCallback((next) => { setSelected(next); localStorage.setItem(storageKey(email), JSON.stringify(next)); }, [email]);
  return [selected, save];
}

function KpiCard({ def, data, loading, index }) {
  const Icon = def.icon;
  return (
    <div data-testid={`kpi-card-${def.key}`} className="admin-card admin-rise relative overflow-hidden" style={{ animationDelay: `${index * 50}ms` }}>
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-[#C9A84C]/5 pointer-events-none" />
      <div className="flex items-center justify-between">
        <span className="font-grotesk text-sm uppercase tracking-wider text-[#888888]">{def.label}</span>
        <span className="grid place-items-center w-10 h-10 rounded-lg bg-[#C9A84C]/10 border border-[#C9A84C]/30"><Icon size={18} className="text-[#C9A84C]" /></span>
      </div>
      <div className="font-serif text-3xl md:text-4xl text-[#F5F5F5] mt-5" data-testid={`kpi-value-${def.key}`}>
        {loading ? <span className="inline-block h-8 w-28 rounded bg-[#242424] animate-pulse" /> : def.value(data)}
      </div>
      <div className="text-sm text-[#888888] mt-2">{def.hint}</div>
    </div>
  );
}

function Picker({ selected, onSave, onClose }) {
  const [draft, setDraft] = useState(selected);
  const [drag, setDrag] = useState(null);
  const toggle = (k) => setDraft((d) => d.includes(k) ? d.filter((x) => x !== k) : d.length >= MAX ? d : [...d, k]);
  const move = (from, to) => setDraft((d) => { if (to < 0 || to >= d.length) return d; const n = [...d]; const [x] = n.splice(from, 1); n.splice(to, 0, x); return n; });
  useEffect(() => { const h = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="kpi-picker">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-4xl bg-[#141414] border border-[#333333] rounded-2xl shadow-2xl admin-rise">
        <div className="flex items-center justify-between px-8 py-6 border-b border-[#262626]">
          <div>
            <h3 className="text-base font-bold text-[#F5F5F5]">Edit KPI layout</h3>
            <p className="text-sm text-[#888888] mt-1">Choose up to {MAX} indicators. Drag or use the arrows to set their order.</p>
          </div>
          <button data-testid="kpi-picker-close" onClick={onClose} className="text-[#888888] hover:text-[#F5F5F5] transition-colors"><X size={20} /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-8 px-8 py-6">
          <div>
            <div className="text-sm font-semibold text-[#C9A84C] uppercase tracking-wider mb-4">Available indicators</div>
            <div className="space-y-2">
              {KPI_DEFS.map((def) => {
                const on = draft.includes(def.key); const Icon = def.icon; const full = !on && draft.length >= MAX;
                return (
                  <button key={def.key} data-testid={`kpi-option-${def.key}`} disabled={full} onClick={() => toggle(def.key)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left text-sm transition-colors duration-200 ${on ? "border-[#C9A84C]/50 bg-[#C9A84C]/10 text-[#F5F5F5]" : "border-[#262626] text-[#888888] hover:border-[#333333] hover:text-[#F5F5F5]"} disabled:opacity-40`}>
                    <Icon size={16} className="text-[#C9A84C]" /><span className="flex-1">{def.label}</span>
                    {on ? <Check size={16} className="text-[#C9A84C]" /> : <Plus size={16} />}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-[#C9A84C] uppercase tracking-wider">On your dashboard</span>
              <span className="text-sm text-[#888888]" data-testid="kpi-picker-count">{draft.length} / {MAX}</span>
            </div>
            <div className="space-y-2 min-h-[200px]">
              {draft.map((k, i) => {
                const def = KPI_DEFS.find((d) => d.key === k); const Icon = def.icon;
                return (
                  <div key={k} draggable data-testid={`kpi-selected-${k}`}
                    onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (drag !== null) move(drag, i); setDrag(null); }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border bg-[#1A1A1A] text-sm text-[#F5F5F5] ${drag === i ? "border-[#C9A84C]" : "border-[#262626]"}`}>
                    <GripVertical size={16} className="text-[#888888] cursor-grab" />
                    <span className="text-[#888888] w-5">{i + 1}</span>
                    <Icon size={16} className="text-[#C9A84C]" /><span className="flex-1">{def.label}</span>
                    <button data-testid={`kpi-up-${k}`} onClick={() => move(i, i - 1)} className="text-[#888888] hover:text-[#C9A84C] disabled:opacity-30" disabled={i === 0}><ArrowUp size={16} /></button>
                    <button data-testid={`kpi-down-${k}`} onClick={() => move(i, i + 1)} className="text-[#888888] hover:text-[#C9A84C] disabled:opacity-30" disabled={i === draft.length - 1}><ArrowDown size={16} /></button>
                    <button data-testid={`kpi-remove-${k}`} onClick={() => toggle(k)} className="text-[#888888] hover:text-[#EF4444]"><X size={16} /></button>
                  </div>
                );
              })}
              {!draft.length && <div className="text-sm text-[#888888] border border-dashed border-[#333333] rounded-lg p-6 text-center">Pick at least one indicator.</div>}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-[#262626]">
          <button data-testid="kpi-picker-reset" onClick={() => setDraft(DEFAULT_KPIS)} className="text-sm text-[#888888] hover:text-[#F5F5F5] px-4 py-2">Reset to default</button>
          <button data-testid="kpi-picker-cancel" onClick={onClose} className="text-sm text-[#F5F5F5] border border-[#333333] rounded-lg px-5 py-2.5 hover:border-[#C9A84C] transition-colors">Cancel</button>
          <button data-testid="kpi-picker-save" disabled={!draft.length} onClick={() => { onSave(draft); onClose(); }} className="text-sm font-semibold text-[#0D0D0D] bg-[#C9A84C] rounded-lg px-6 py-2.5 hover:bg-[#D8B75B] transition-colors disabled:opacity-40">Save layout</button>
        </div>
      </div>
    </div>
  );
}

export function KpiDashboard({ api, email }) {
  const [selected, save] = useKpiPrefs(email);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const defs = useMemo(() => selected.map((k) => KPI_DEFS.find((d) => d.key === k)).filter(Boolean), [selected]);

  useEffect(() => {
    const need = new Set(defs.map((d) => d.source));
    if (need.has("rfqs")) need.add("quoteRequests");
    const keys = [...need];
    setLoading(true);
    Promise.allSettled(keys.map((k) => api.get(SOURCES[k]))).then((rs) => {
      const next = {};
      rs.forEach((r, i) => { if (r.status === "fulfilled") next[keys[i]] = r.value.data; });
      setData((d) => ({ ...d, ...next }));
    }).finally(() => setLoading(false));
  }, [api, defs]);

  return (
    <section data-testid="kpi-section" className="mb-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-base font-bold text-[#F5F5F5]">Key performance indicators</h2>
          <p className="text-sm text-[#888888] mt-1">Personalised for {email} · {defs.length} of {MAX} slots in use</p>
        </div>
        <button data-testid="kpi-edit-layout" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-lg border border-[#C9A84C]/50 text-[#C9A84C] px-4 py-2.5 text-sm font-semibold hover:bg-[#C9A84C] hover:text-[#0D0D0D] transition-colors duration-200">
          <Pencil size={16} /> Edit Layout
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="kpi-grid">
        {defs.map((def, i) => <KpiCard key={def.key} def={def} data={data} loading={loading} index={i} />)}
      </div>
      {open && <Picker selected={selected} onSave={save} onClose={() => setOpen(false)} />}
    </section>
  );
}
